/**
 * Profile API endpoints
 * Handles user profile management including:
 * - Profile updates (name, sendFrom, etc.)
 * - Avatar and logo management with image cropping
 * - Contact method (email/phone) management
 */

import type { FileRef } from '@/types/file';
import { apiClient } from './client';
import type { User, ImageCrop } from '@/types';

// Types for profile operations

export interface UpdateProfileRequest {
  firstName?: string;
  lastName?: string;
  sendFrom?: string;
  crmBccEmail?: string;
  timezone?: string;
  avatar?: {
    key?: string;
    crop?: ImageCrop;
  };
  organization?: {
    logo?: {
      key?: string;
      crop?: ImageCrop;
    };
  };
}

export interface UpdateAvatarRequest {
  avatar: {
    key: string;
    crop: ImageCrop;
  };
}

export interface RemoveAvatarRequest {
  avatar: Record<string, never>;  // Empty object to remove avatar
}

/** Response from PUT /api/context/logo/ — the new (possibly null) logo URL. */
export interface ContextLogoResponse {
  logo: FileRef | null;
}

/** Response from PUT /api/context/header-logo/ — the new (possibly null) header-logo URL. */
export interface ContextHeaderLogoResponse {
  headerLogo: FileRef | null;
}

/** Response from GET/PUT /api/context/badge-logo/ — the new (possibly null) badge-logo. */
export interface ContextBadgeLogoResponse {
  badgeLogo: FileRef | null;
}

export interface AddContactMethodRequest {
  email?: string;
  phone?: string;
}

export interface VerifyContactMethodRequest {
  email?: string;
  phone?: string;
  verificationCode: string;
}

export interface RemoveContactMethodRequest {
  email?: string;
  phone?: string;
}

// API Functions

/**
 * Update user profile fields
 * PUT /api/auth/update/
 */
export async function updateProfile(data: UpdateProfileRequest): Promise<User> {
  const response = await apiClient.put<User>('/api/auth/update/', data);
  return response.data;
}

/**
 * Update user avatar with file key and crop data
 * PUT /api/auth/update/ with avatar_data
 */
export async function updateAvatar(fileKey: string, crop: ImageCrop): Promise<User> {
  const data: UpdateAvatarRequest = {
    avatar: {
      key: fileKey,
      crop,
    },
  };
  const response = await apiClient.put<User>('/api/auth/update/', data);
  return response.data;
}

/**
 * Remove user avatar
 * PUT /api/auth/update/ with avatar: {}
 */
export async function removeAvatar(): Promise<User> {
  const data: RemoveAvatarRequest = {
    avatar: {},
  };
  const response = await apiClient.put<User>('/api/auth/update/', data);
  return response.data;
}

/**
 * Set the logo for the user's CURRENT context (organization or network).
 * PUT /api/context/logo/ with { key, crop }.
 *
 * The endpoint is context-aware (org vs network resolved server-side) and
 * permission-gated: the membership must have can_edit_logo, or the user is
 * staff. Returns the new 3:1 logo URL. See uptake #4099.
 */
export async function updateContextLogo(fileKey: string, crop: ImageCrop): Promise<ContextLogoResponse> {
  const response = await apiClient.put<ContextLogoResponse>('/api/context/logo/', {
    key: fileKey,
    crop,
  });
  return response.data;
}

/**
 * Clear the logo for the user's CURRENT context (organization or network).
 * PUT /api/context/logo/ with an empty body (omitting `key` clears the logo).
 */
export async function removeContextLogo(): Promise<ContextLogoResponse> {
  const response = await apiClient.put<ContextLogoResponse>('/api/context/logo/', {});
  return response.data;
}

/** Response from PATCH /api/context/ — the updated context name. */
export interface ContextNameResponse {
  name: string | null;
}

/**
 * Rename the user's CURRENT context (org or network).
 * PATCH /api/context/ with { name }. Operates on the active context resolved
 * server-side (no id from the client), exactly like the logo endpoint, and is
 * gated by the same rule: the membership's can_edit_logo, or staff. The
 * context's identifier (URL slug) is never changed. Callers refetch /me to
 * refresh the stored context.
 */
export async function updateContextName(name: string): Promise<ContextNameResponse> {
  const response = await apiClient.patch<ContextNameResponse>('/api/context/', { name });
  return response.data;
}

/**
 * Set the transparent header logo for the user's CURRENT NETWORK context.
 * PUT /api/context/header-logo/ with { key }.
 *
 * Network-only branding asset (orgs keep just the 3:1 banner). Unlike the
 * banner there is no server-side crop — upload a pre-sized transparent PNG —
 * and it is served as the ORIGINAL asset (never Thumbor) to preserve alpha.
 * Permission-gated: the membership must have can_edit_logo, or the user is
 * staff. Returns the new header-logo URL. See network-branding (#4099 family).
 */
export async function updateContextHeaderLogo(fileKey: string): Promise<ContextHeaderLogoResponse> {
  const response = await apiClient.put<ContextHeaderLogoResponse>('/api/context/header-logo/', {
    key: fileKey,
  });
  return response.data;
}

/**
 * Clear the header logo for the user's CURRENT NETWORK context.
 * PUT /api/context/header-logo/ with an empty body (omitting `key` clears it).
 */
export async function removeContextHeaderLogo(): Promise<ContextHeaderLogoResponse> {
  const response = await apiClient.put<ContextHeaderLogoResponse>('/api/context/header-logo/', {});
  return response.data;
}

/**
 * Set the badge logo for the user's CURRENT NETWORK context.
 * PUT /api/context/badge-logo/ with { key }.
 *
 * Network-only branding asset — a small square stamp shown on content cards to
 * identify network-granted content. Upload a pre-sized square PNG. Like the
 * header logo: no crop, served as the ORIGINAL asset (never Thumbor).
 * Permission-gated: the membership must have can_edit_logo, or the user is
 * staff (same rule as the header logo).
 */
export async function updateContextBadgeLogo(fileKey: string): Promise<ContextBadgeLogoResponse> {
  const response = await apiClient.put<ContextBadgeLogoResponse>('/api/context/badge-logo/', {
    key: fileKey,
  });
  return response.data;
}

/**
 * Clear the badge logo for the user's CURRENT NETWORK context.
 * PUT /api/context/badge-logo/ with an empty body (omitting `key` clears it).
 */
export async function removeContextBadgeLogo(): Promise<ContextBadgeLogoResponse> {
  const response = await apiClient.put<ContextBadgeLogoResponse>('/api/context/badge-logo/', {});
  return response.data;
}

/**
 * Per-network NOVI SSO config for the network-admin editor (same area/permission
 * as the logos). The client secret is write-only — the API never returns it, only
 * whether one is stored (`clientSecretSet`).
 */
export interface NoviSsoConfig {
  enabled: boolean;
  baseUrl: string | null;
  clientId: string | null;
  clientSecretSet: boolean;
  label: string | null;
  logo: FileRef | null;
}

export interface UpdateNoviSsoInput {
  enabled: boolean;
  baseUrl: string | null;
  clientId: string | null;
  /** Write-only: provide to set the secret; omit to keep the stored one. */
  clientSecret?: string;
  label: string | null;
  /**
   * S3 key for the button icon: a key sets it, `null` clears it, and `undefined`
   * (omitted) leaves the existing icon untouched.
   */
  logoKey?: string | null;
}

/** GET /api/context/novi-sso/ — current config for the editor (secret masked). */
export async function getContextNoviSso(): Promise<NoviSsoConfig> {
  const response = await apiClient.get<NoviSsoConfig>('/api/context/novi-sso/');
  return response.data;
}

/**
 * PUT /api/context/novi-sso/ — set the current NETWORK's NOVI SSO config.
 *
 * Request body is snake_case (the backend ValidatedBody isn't camel-aliased).
 * `clientSecret`/`logoKey` are only sent when present so the backend's
 * keep-unchanged semantics apply.
 */
export async function updateContextNoviSso(input: UpdateNoviSsoInput): Promise<NoviSsoConfig> {
  const body: Record<string, unknown> = {
    enabled: input.enabled,
    base_url: input.baseUrl,
    client_id: input.clientId,
    label: input.label,
  };
  if (input.clientSecret !== undefined) body.client_secret = input.clientSecret;
  if (input.logoKey !== undefined) body.logo_key = input.logoKey;
  const response = await apiClient.put<NoviSsoConfig>('/api/context/novi-sso/', body);
  return response.data;
}

/**
 * Add a new contact method (email or phone)
 * POST /api/auth/contact-method/
 * Sends verification code to the provided email/phone
 */
export async function addContactMethod(data: AddContactMethodRequest): Promise<void> {
  await apiClient.post('/api/auth/contact-method/', data);
}

/**
 * Remove a contact method
 * DELETE /api/auth/contact-method/
 */
export async function removeContactMethod(data: RemoveContactMethodRequest): Promise<void> {
  await apiClient.delete('/api/auth/contact-method/', { data });
}

/**
 * Verify a contact method with verification code
 * POST /api/auth/verify/
 */
export async function verifyContactMethod(data: VerifyContactMethodRequest): Promise<void> {
  await apiClient.post('/api/auth/verify/', data);
}

